import {useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import Swal from 'sweetalert2';
import {fetchPatientDetailsUuid, postPatientReport} from '../Redux/Thunk/Actions';
import '../styles/PatientReportFileTemplateCreate.scss';
import FigmaHeader from './FigmaHeader/FigmaHeader';

// Common styles
const commonStyles = {
  input: {
    border: '1px solid #b0a4a4ff',
    outline: 'none',
    padding: '10px',
    backgroundColor: 'white',
    width: '100%'
  },
  label: {
    fontWeight: 600
  },
  separator: {
    fontWeight: 600,
    fontSize: '18px'
  },
  section: {
    maxWidth: '97%',
    margin: '0 auto'
  }
};

// Cryopreservation Indication options
const cryopreservationIndications = [
  {value: '', label: 'Select Indication'},
  {value: 'IVF/ICSI Backup', label: 'Backup for IVF/ICSI'},
  {value: 'Pre-treatment', label: 'Pre-treatment (Chemo/Radiation/Surgery)'},
  {value: 'Travel/Timing', label: 'Travel/Timing Constraint'},
  {value: 'Donor', label: 'Donor Sperm'},
  {value: 'Other', label: 'Other'}
];

// Section header component
const SectionHeader = ({title, className = 'spermiogram-head'}) => (
  <h2
    style={{
      textAlign: 'center',
      backgroundColor: '#f7f7f7',
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      fontSize: '20px',
      fontWeight: '600',
      padding: '10px 0 10px 30px',
      margin: '16px 0'
    }}
    className={className}>
    {title}
  </h2>
);

// Reusable FormField component
const FormField = ({label, type = 'text', name, value, onChange, show, options = [], width = '200px', labelWidth = '160px', reference}) => {
  const inputStyle = {...commonStyles.input, width};

  if (show) {
    // Display mode (read-only)
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `${labelWidth} 16px 1fr`,
          alignItems: 'center',
          gap: '16px'
        }}>
        <span style={{...commonStyles.label, whiteSpace: 'nowrap', fontSize: '14px'}}>{label}</span>
        <span style={commonStyles.separator}>:</span>
        <span style={{fontSize: '14px', textAlign: 'left'}}>{value}</span>
      </div>
    );
  }

  // Edit mode
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `${labelWidth} 16px ${width}`,
        alignItems: 'center',
        gap: '16px'
      }}>
      <span style={{...commonStyles.label, whiteSpace: 'nowrap', fontSize: '14px'}}>{label}</span>
      <span style={commonStyles.separator}>:</span>

      {type === 'select' ? (
        <select name={name} value={value} onChange={onChange} style={inputStyle}>
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input type={type} name={name} value={value} onChange={onChange} style={inputStyle} />
      )}
    </div>
  );
};

// Patient Info Section component
const PatientInfoSection = ({patientNumber, partnerType, show, handleChange, valueChange}) => {
  const genderOptions = [
    {value: '', label: 'Select Gender'},
    {value: 'Male', label: 'Male'},
    {value: 'Female', label: 'Female'},
    {value: 'Others', label: 'Others'},
    {value: 'Decline to State', label: 'Decline to State'}
  ];

  return (
    <div className='' style={{width: '100%', display: 'block'}} id={`patientInfo${patientNumber}`}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          gap: '3px',
          backgroundColor: '#f7f7f7 ',
          borderRadius: '6px',
          padding: '16px 8px 16px 8px',
          gap: '3px'
        }}>
        {/* Patient ID */}
        <FormField label='Patient ID' name={`patientId${patientNumber}`} value={valueChange[`patientId${patientNumber}`]} onChange={handleChange} show={show} width='250px' labelWidth='125px' />

        {/* Patient Name */}
        <FormField
          label={`${partnerType} Partner Name`}
          name={`patientName${patientNumber}`}
          value={valueChange[`patientName${patientNumber}`]}
          onChange={handleChange}
          show={show}
          width='250px'
          labelWidth='125px'
        />

        {/* Gender */}
        <FormField
          label='Gender'
          type='select'
          name={`gender${patientNumber}`}
          value={valueChange[`gender${patientNumber}`]}
          onChange={handleChange}
          show={show}
          options={genderOptions}
          width='250px'
          labelWidth='125px'
        />

        {/* Age */}
        <FormField label='Age' name={`age${patientNumber}`} value={valueChange[`age${patientNumber}`]} onChange={handleChange} show={show} width='250px' labelWidth='125px' />
      </div>
    </div>
  );
};

function SemenFreezing(props) {
  const [show, setshow] = useState(false);
  const Content = useRef();
  const [valueChange, setvalueChange] = useState({
    gender1: 'Male',
    gender2: 'Female',

    // Collection details
    dateOfCollection: '',
    timeOfCollection: '',
    timeOfCollectionHour: '',
    timeOfCollectionMinute: '',
    timeOfCollectionPeriod: 'AM',
    timeOfAnalysis: '',
    timeOfAnalysisHour: '',
    timeOfAnalysisMinute: '',
    timeOfAnalysisPeriod: 'AM',
    liquefactionTime: '',
    abstinencePeriod: '',

    // Indication for cryopreservation
    cryopreservationIndication: '',
    otherIndication: '',

    // Semen Analysis - Pre-freezing
    volume: '',
    appearance: '',
    viscosity: '',
    liquefactionTime: '',
    pH: '',
    spermConcentration: '',
    totalSpermCount: '',
    progressiveMotility: '',
    nonProgressiveMotility: '',
    immotile: '',
    morphology: '',
    vitality: '',
    totalMotileSpermCount: '',

    // Diagnosis
    diagnosis: '',

    // Cryopreservation details
    vialsFrozen: '',
    strawsFrozen: '',
    semenValidity: '',
    cryoprotectantUsed: '',

    // Reporting
    reportingTechnician: ''
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [patientOne_UUId, setPatientOne_UUId] = useState('');
  const [patientTwo_UUId, setPatientTwo_UUId] = useState('');

  const handleChange = e => {
    const text = e.target.value;

    if (e.target.name === 'patientId1' || e.target.name === 'patientId2') {
      if (text.length === 10 && text.charAt(0) === 'A') {
        const fetchData = async () => {
          const payload = {p_uhid: text};
          props.loaderchange('true');
          const addPatient = fetchPatientDetailsUuid(payload);
          const response = await dispatch(addPatient);
          if (response.payload?.status?.code === 200) {
            const isSinglePatient = response.payload.data.data.length === 1;
            if (isSinglePatient) {
              const data = response.payload.data.data[0];
              const namePat1 = data?.titles + ' ' + data?.first_name + ' ' + data?.last_name;

              if (e.target.name === 'patientId1') {
                setPatientOne_UUId(data?.id);
                setvalueChange({
                  ...valueChange,
                  patientName1: namePat1,
                  age1: data?.age,
                  gender1: data?.gender,
                  patientId1: text
                });
              } else {
                setPatientTwo_UUId(data?.id);
                setvalueChange({
                  ...valueChange,
                  patientName2: namePat1,
                  age2: data?.age,
                  gender2: data?.gender,
                  patientId2: text
                });
              }
            } else {
              const data = response.payload.data.data[0].gender === 'Male' ? response.payload.data.data[0] : response.payload.data.data[1];
              const data2 = response.payload.data.data[1].gender === 'Female' ? response.payload.data.data[1] : response.payload.data.data[0];
              const namePat1 = data?.titles + ' ' + data?.first_name + ' ' + data?.last_name;
              const namePat2 = data2?.titles + ' ' + data2?.first_name + ' ' + data2?.last_name;

              setPatientOne_UUId(data?.id);
              setPatientTwo_UUId(data2?.id);
              setvalueChange({
                ...valueChange,
                patientName1: namePat1,
                patientName2: namePat2,
                age1: data?.age,
                age2: data2?.age,
                gender1: data?.gender,
                gender2: data2?.gender,
                patientId1: data?.p_uhid,
                patientId2: data2?.p_uhid
              });
            }
            props.loaderchange('false');
          } else {
            props.loaderchange('false');
            props.popupalert('true');
            props.popuptext('Network went wrong, Please try after some time.');
            setTimeout(() => {
              props.popupalert('false');
            }, 2000);
          }
        };
        fetchData();
      } else {
        setvalueChange({
          ...valueChange,
          [e.target.name]: text
        });
      }
    } else {
      setvalueChange({...valueChange, [e.target.name]: text});
    }
  };

  const handletest = async () => {
    try {
      if (
        valueChange.patientOne_UUId !== '' &&
        valueChange.patientId1 !== '' &&
        valueChange.patientId1.length === 10 &&
        (valueChange.patientId2 !== '' ? valueChange.patientId1 !== valueChange.patientId2 : true)
      ) {
        setshow(true);
        setTimeout(async () => {
          const htmlPayload = document.getElementById('reportcreate').innerHTML;
          const payload = {
            report_template: htmlPayload,
            patient_id: patientOne_UUId,
            description: '',
            patient_two: patientTwo_UUId,
            couple_id: valueChange.coupleId,
            template_type: 'Sperm Report'
          };

          props.loaderchange(true);
          const reportResponse = await dispatch(postPatientReport({type: '2', payload: payload}));

          if (reportResponse?.payload?.status?.code === 200) {
            props.loaderchange(false);
            navigate('/patientreports');
            Swal.fire({
              title: 'Successful.',
              text: 'Report added successful.',
              icon: 'success',
              showConfirmButton: false,
              timer: 1500
            });
          } else {
            setshow(false);
            props.loaderchange(false);
            props.popupalert(true);
            props.popuptext('Network went wrong, Please try after some time.');
            setTimeout(() => {
              props.popupalert(false);
            }, 2000);
          }
        }, 3000);
      } else {
        Swal.fire({
          title: 'Error.',
          text: 'Please enter valid patient id.',
          icon: 'error',
          showConfirmButton: false,
          timer: 1500
        });
      }
    } catch (error) {
      console.error('An error occurred:', error);
    }
  };

  const sideactive = useSelector(state => state.counter.sidebarnav);

  return (
    <>
      <div className={sideactive ? 'maincontainer-patient-sa' : 'maincontainer-patient'}>
        <div className='row'>
          <div className='col'>
            <h3 className='page-title'>Report Template</h3>
            <ul className='breadcrumb'>
              <li className='breadcrumb-item'>
                <a href='/' style={{textDecoration: 'none', color: 'black'}}>
                  Dashboard /
                </a>
                <a href='/patientreports' style={{textDecoration: 'none', color: 'black'}}>
                  &nbsp;Patient Reports
                </a>
              </li>
              <li className='breadcrumb-item active'>Report Template</li>
            </ul>
          </div>
        </div>
        <h1>🧊 SEMEN FREEZING REPORT</h1>
        <div style={{backgroundColor: 'white', maxWidth: '100%'}} className='patientreport' id='reportcreate'>
          {/* Patient Information Header */}
          <FigmaHeader />

          {/* Patient & Couple Details */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              gap: '3px',
              border: '1px solid #000',
              marginBottom: '20px'
            }}
            className='report-template-wrap1'>
            <PatientInfoSection patientNumber={1} partnerType='Male' show={show} handleChange={handleChange} valueChange={valueChange} />
            <div className='patientreportcreate2' style={{width: '100%', height: '100%', backgroundColor: '#f7f7f7'}} id='patientInfo2'>
              <PatientInfoSection patientNumber={2} partnerType='Female' show={show} handleChange={handleChange} valueChange={valueChange} />
            </div>
          </div>

          <h2 style={{textAlign: 'center', fontWeight: '600', backgroundColor: '#f7f7f7', padding: '10px'}}>SEMEN FREEZING REPORT </h2>

          {/* Collection Details */}
          <div style={{marginBottom: '20px'}}>
            <SectionHeader title='🔬 COLLECTION DETAILS' />
            <div style={{padding: '20px'}}>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px'}}>
                <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
                  <FormField
                    label='Date of Collection:'
                    type='date'
                    name='dateOfCollection'
                    value={valueChange.dateOfCollection}
                    onChange={handleChange}
                    show={show}
                    width='200px'
                    labelWidth='160px'
                  />
                  {/* Custom Time of Collection field with 12-hour format */}
                  {!show ? (
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '160px 16px 120px 60px 60px',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                      <span style={{...commonStyles.label, whiteSpace: 'nowrap', fontSize: '14px'}}>Time of Collection:</span>
                      <span style={commonStyles.separator}>:</span>
                      <div style={{display: 'flex', alignItems: 'center', gap: '10px', paddingLeft: '15px'}}>
                        <input
                          type='number'
                          min='1'
                          max='12'
                          placeholder='hh'
                          name='timeOfCollectionHour'
                          value={valueChange.timeOfCollectionHour || ''}
                          onChange={handleChange}
                          style={{...commonStyles.input, width: '50px', textAlign: 'center'}}
                        />
                        <span>:</span>
                        <input
                          type='number'
                          min='0'
                          max='59'
                          placeholder='mm'
                          name='timeOfCollectionMinute'
                          value={valueChange.timeOfCollectionMinute || ''}
                          onChange={handleChange}
                          style={{...commonStyles.input, width: '50px', textAlign: 'center'}}
                        />
                        <select name='timeOfCollectionPeriod' value={valueChange.timeOfCollectionPeriod || 'AM'} onChange={handleChange} style={{...commonStyles.input, width: '80px'}}>
                          <option value='AM'>AM</option>
                          <option value='PM'>PM</option>
                        </select>
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '160px 16px 1fr',
                        alignItems: 'center',
                        gap: '16px'
                      }}>
                      <span style={{...commonStyles.label, whiteSpace: 'nowrap', fontSize: '14px'}}>Time of Collection:</span>
                      <span style={commonStyles.separator}>:</span>
                      <span style={{fontSize: '14px', textAlign: 'left'}}>
                        {valueChange.timeOfCollectionHour && valueChange.timeOfCollectionMinute
                          ? valueChange.timeOfCollectionPeriod
                            ? `${valueChange.timeOfCollectionHour}:${valueChange.timeOfCollectionMinute.toString().padStart(2, '0')} ${valueChange.timeOfCollectionPeriod}`
                            : `${valueChange.timeOfCollectionHour}:${valueChange.timeOfCollectionMinute.toString().padStart(2, '0')}`
                          : ''}
                      </span>
                    </div>
                  )}
                  {!show ? (
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '160px 16px 120px 60px 60px',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                      <span style={{...commonStyles.label, whiteSpace: 'nowrap', fontSize: '14px'}}>Time of Analysis:</span>
                      <span style={commonStyles.separator}>:</span>
                      <div style={{display: 'flex', alignItems: 'center', gap: '10px', paddingLeft: '15px'}}>
                        <input
                          type='number'
                          min='1'
                          max='12'
                          placeholder='hh'
                          name='timeOfAnalysisHour'
                          value={valueChange.timeOfAnalysisHour || ''}
                          onChange={handleChange}
                          style={{...commonStyles.input, width: '50px', textAlign: 'center'}}
                        />
                        <span>:</span>
                        <input
                          type='number'
                          min='0'
                          max='59'
                          placeholder='mm'
                          name='timeOfAnalysisMinute'
                          value={valueChange.timeOfAnalysisMinute || ''}
                          onChange={handleChange}
                          style={{...commonStyles.input, width: '50px', textAlign: 'center'}}
                        />
                        <select name='timeOfAnalysisPeriod' value={valueChange.timeOfAnalysisPeriod || 'AM'} onChange={handleChange} style={{...commonStyles.input, width: '80px'}}>
                          <option value='AM'>AM</option>
                          <option value='PM'>PM</option>
                        </select>
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '160px 16px 1fr',
                        alignItems: 'center',
                        gap: '16px'
                      }}>
                      <span style={{...commonStyles.label, whiteSpace: 'nowrap', fontSize: '14px'}}>Time of Analysis:</span>
                      <span style={commonStyles.separator}>:</span>
                      <span style={{fontSize: '14px', textAlign: 'left'}}>
                        {valueChange.timeOfAnalysisHour && valueChange.timeOfAnalysisMinute
                          ? valueChange.timeOfAnalysisPeriod
                            ? `${valueChange.timeOfAnalysisHour}:${valueChange.timeOfAnalysisMinute.toString().padStart(2, '0')} ${valueChange.timeOfAnalysisPeriod}`
                            : `${valueChange.timeOfAnalysisHour}:${valueChange.timeOfAnalysisMinute.toString().padStart(2, '0')}`
                          : ''}
                      </span>
                    </div>
                  )}
                </div>

                <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
                  <FormField label='Liquefaction Time:' name='liquefactionTime' value={valueChange.liquefactionTime} onChange={handleChange} show={show} width='200px' labelWidth='200px' />

                  {/* Custom Abstinence Period field with "days" after input */}
                  {!show ? (
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '200px 16px 200px auto',
                        alignItems: 'center',
                        gap: '16px'
                      }}>
                      <span style={{...commonStyles.label, whiteSpace: 'nowrap', fontSize: '14px'}}>Abstinence Period:</span>
                      <span style={commonStyles.separator}>:</span>
                      <input
                        type='text'
                        name='abstinencePeriod'
                        value={valueChange.abstinencePeriod}
                        onChange={handleChange}
                        style={{
                          ...commonStyles.input,
                          width: '200px'
                        }}
                      />
                      <span style={{fontSize: '14px'}}>days</span>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '200px 16px 1fr',
                        alignItems: 'center',
                        gap: '16px'
                      }}>
                      <span style={{...commonStyles.label, whiteSpace: 'nowrap', fontSize: '14px'}}>Abstinence Period:</span>
                      <span style={commonStyles.separator}>:</span>
                      <span style={{fontSize: '14px', textAlign: 'left'}}>{valueChange.abstinencePeriod} days</span>
                    </div>
                  )}

                  {/* Indication for Cryopreservation field */}
                  <FormField
                    label='Indication for Cryopreservation'
                    type='select'
                    name='cryopreservationIndication'
                    value={valueChange.cryopreservationIndication}
                    onChange={handleChange}
                    show={show}
                    options={cryopreservationIndications}
                    width='200px'
                    labelWidth='200px'
                  />
                  {valueChange.cryopreservationIndication === 'Other' && (
                    <FormField label='Other Indication:' name='otherIndication' value={valueChange.otherIndication} onChange={handleChange} show={show} width='200px' labelWidth='200px' />
                  )}
                </div>
              </div>
            </div>

            {/* Semen Analysis Table */}
            <div style={{marginBottom: '20px'}}>
              <SectionHeader title='🔬 SEMEN ANALYSIS – PRE-FREEZING PARAMETERS' />
              <table style={{width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd'}}>
                <thead>
                  <tr style={{backgroundColor: '#f0f0f0'}}>
                    <th style={{padding: '8px', border: '1px solid #ddd', textAlign: 'left'}}>Parameter</th>
                    <th style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>Result</th>
                    <th style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>WHO Reference</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Volume</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='volume'
                        value={valueChange.volume || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.volume}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>{`>=1.4ml`}</td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Appearance</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='appearance'
                        value={valueChange.appearance || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.appearance}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>Opalescent, grey-white</td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Viscosity</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='viscosity'
                        value={valueChange.viscosity || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.viscosity}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}></td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Liquefaction Time</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='liquefactionTime'
                        value={valueChange.liquefactionTime || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.liquefactionTime}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}></td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>pH</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='pH'
                        value={valueChange.pH || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.pH}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}></td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Sperm Concentration</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='spermConcentration'
                        value={valueChange.spermConcentration || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.spermConcentration}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>{`>=16 millions`}</td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Total Sperm Count</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='totalSpermCount'
                        value={valueChange.totalSpermCount || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.totalSpermCount}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>{`>=39 millions`}</td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Progressive Motility (PR)</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='progressiveMotility'
                        value={valueChange.progressiveMotility || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.progressiveMotility}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>{`>=30%`}</td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Non-Progressive Motility (NP)</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='nonProgressiveMotility'
                        value={valueChange.nonProgressiveMotility || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.nonProgressiveMotility}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>1%</td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Immotile</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='immotile'
                        value={valueChange.immotile || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.immotile}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>20%</td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Morphology (Strict Criteria)</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='morphology'
                        value={valueChange.morphology || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.morphology}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}></td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Vitality (Eosin-Nigrosin) (%)</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='vitality'
                        value={valueChange.vitality || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.vitality}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>{`>= 54%`}</td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Total Motile Sperm Count (TMSC)</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='totalMotileSpermCount'
                        value={valueChange.totalMotileSpermCount || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.totalMotileSpermCount}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>{`>= 42%`}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Second table for Morphology and Vitality */}
            {/* <div style={{marginBottom: '20px'}}>
              <table style={{width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd'}}>
                <thead>
                  <tr style={{backgroundColor: '#f0f0f0'}}>
                    <th style={{padding: '8px', border: '1px solid #ddd', textAlign: 'left'}}>Parameter</th>
                    <th style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>Result</th>
                    <th style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>WHO Reference</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Morphology (Strict Criteria)</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='morphology'
                        value={valueChange.morphology || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.morphology}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>{``}</td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Vitality (Eosin-Nigrosin) (%)</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='vitality'
                        value={valueChange.vitality || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.vitality}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>{`>= 54%`}</td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Total Motile Sperm Count (TMSC)</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='totalMotileSpermCount'
                        value={valueChange.totalMotileSpermCount || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '150px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.totalMotileSpermCount}</span>
                    </td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>{`>= 42%`}</td>
                  </tr>
                </tbody>
              </table>
            </div> */}

            {/* Diagnosis */}
            <div style={{marginBottom: '10px'}}>
              <SectionHeader title='📋 DIAGNOSIS' inline={true} />
              {!show ? (
                <textarea
                  name='diagnosis'
                  value={valueChange.diagnosis}
                  onChange={handleChange}
                  style={{
                    ...commonStyles.input,
                    width: '100%',
                    minHeight: '60px',
                    padding: '10px',
                    fontFamily: 'Arial, sans-serif',
                    marginTop: '5px'
                  }}
                  placeholder='Enter clinical diagnosis and notes...'
                />
              ) : (
                valueChange.diagnosis && (
                  <div
                    style={{
                      border: '1px solid #ddd',
                      padding: '10px',
                      minHeight: '40px',
                      backgroundColor: '#f9f9f9',
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'Arial, sans-serif',
                      marginTop: '5px'
                    }}>
                    {valueChange.diagnosis}
                  </div>
                )
              )}
            </div>

            {/* Cryopreservation Details */}
            <div style={{marginBottom: '20px'}}>
              <SectionHeader title='🧊 CRYOPRESERVATION DETAILS' />
              <table style={{width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd'}}>
                <thead>
                  <tr style={{backgroundColor: '#f0f0f0'}}>
                    <th style={{padding: '8px', border: '1px solid #ddd', textAlign: 'left'}}>Parameter</th>
                    <th style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>No. of Vials Frozen</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='vialsFrozen'
                        value={valueChange.vialsFrozen || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '120px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.vialsFrozen}</span>
                    </td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>No. of Straws Frozen</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='strawsFrozen'
                        value={valueChange.strawsFrozen || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '120px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.strawsFrozen}</span>
                    </td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Semen Validity</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='semenValidity'
                        value={valueChange.semenValidity || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '120px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.semenValidity}</span>
                    </td>
                  </tr>
                  <tr>
                    <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>Cryoprotectant Used</td>
                    <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'center'}}>
                      <input
                        type='text'
                        className={`createTime ${show ? 'hide' : 'show'}`}
                        name='cryoprotectantUsed'
                        value={valueChange.cryoprotectantUsed || ''}
                        onChange={e => handleChange(e)}
                        style={{...commonStyles.input, width: '120px'}}
                      />
                      <span className={`input ${!show ? 'hide' : 'show'}`}>{valueChange.cryoprotectantUsed}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={{marginTop: '40px', textAlign: 'right'}}>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '50px', justifyContent: 'end'}}>
                <span style={{fontWeight: 600}}>Reporting Andrology Technician:</span>
                {!show ? (
                  <input
                    type='text'
                    name='reportingTechnician'
                    value={valueChange.reportingTechnician}
                    onChange={handleChange}
                    style={{minWidth: '200px', maxWidth: '400px', border: '1px solid #000'}}
                  />
                ) : (
                  <span style={{borderBottom: '1px solid #000', minWidth: '200px', maxWidth: '400px', textAlign: 'left'}}>{valueChange.reportingTechnician}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className='Footer'>
          <button onClick={handletest} className='Button_Create'>
            Add Report
          </button>
        </div>
      </div>
    </>
  );
}

export default SemenFreezing;
